import { useState, useEffect, useRef } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";
import { formatNumber } from "../../../utils/formatNumber";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/th";
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import SubjectIcon from "@mui/icons-material/Subject";
import { getAdjustedPurchaseItems } from "../../../utils/adjustPurchaseItems";

moment.locale("th");

export default function PurchasesSummary() {
  const navigate = useNavigate();
  const printRef = useRef();

  const [salesTransaction, setSalesTransaction] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [search, setSearch] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const res = await services.getPurchaseTransactions();
        const transactionRes = res.data.data;
        setSalesTransaction(transactionRes || []);
      } catch (err) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `รายงานสรุปการซื้อ_${moment().format("YYYY-MM-DD")}`,
  });

  // Prepare data
  const rows = salesTransaction.map((t, index) => {
    const adjustedItems = getAdjustedPurchaseItems(t);
    const totalNetPrice = adjustedItems.reduce(
      (sum, item) => sum + Number(item.netPrice || 0),
      0,
    );

    return {
      id: index,
      transactionId: t.transactionId,
      billNumber: t.billNumber,
      customerName: t.customerName,
      status: t.status,
      paymentMethod: t.paymentMethod ?? "-",
      totalNetPrice,
      createdAt: t.createdAt,
      displayDate: moment(t.createdAt).add(543, "year").format("D MMM YYYY เวลา HH:mm"),
      createdAtDateOnly: moment(t.updatedAt).format("YYYY-MM-DD"), //Logic matches original file
    };
  });

  const filteredRows = rows.filter((row) => {
    const matchesSearch =
      row.billNumber.toLowerCase().includes(search.toLowerCase()) ||
      (row.customerName && row.customerName.toLowerCase().includes(search.toLowerCase())) ||
      row.paymentMethod.toLowerCase().includes(search.toLowerCase());

    const matchesDate = !searchDate || row.createdAtDateOnly === searchDate;

    const matchesStatus =
      !statusFilter || row.status?.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesDate && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "payed": return "bg-green-100 text-green-700 border-green-200";
      case "waiting": return "bg-orange-100 text-orange-700 border-orange-200";
      case "cancel": return "bg-red-100 text-red-700 border-red-200";
      case "sent": return "bg-blue-100 text-blue-700 border-blue-200";
      default: return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const getStatusLabel = (status) => {
      // If there are specific Thai labels for these statuses, they should be mapped here.
      // The original code just displayed the value directly, so we keep that behavior or map if needed.
      // Original code used: `label={value || "-"}`.
      return status || "-";
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <AppBreadcrumbs />
        </div>

        {/* Header & Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col gap-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <span className="w-1 h-8 bg-green-600 rounded-full"></span>
                    รายการรับซื้อสินค้า
                </h1>
                <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-medium shadow-sm"
                >
                    <PrintIcon fontSize="small" />
                    <span>พิมพ์รายงาน</span>
                </button>
            </div>
            
            <div className="flex flex-col md:flex-row gap-4 items-center flex-wrap">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="ลขที่บิล, ชื่อลูกค้า, วิธีชำระเงิน"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                    />
                </div>

                {/* Date Filter */}
                <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                    <span className="text-sm font-medium text-gray-500 whitespace-nowrap">ค้นหาวันที่</span>
                    <input
                        type="date"
                        value={searchDate}
                        onChange={(e) => setSearchDate(e.target.value)}
                        className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm"
                    />
                </div>
            </div>

            {/* Status Tabs */}
            <div className="flex flex-wrap gap-2">
                {[
                    { id: "", label: "ทั้งหมด" },
                    { id: "sent", label: "ส่ง" },
                    { id: "waiting", label: "รอดำเนินการ" },
                    { id: "payed", label: "สำเร็จ" },
                    { id: "cancel", label: "ยกเลิก" },
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setStatusFilter(tab.id)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            statusFilter === tab.id
                                ? "bg-green-600 text-white shadow-md shadow-green-200"
                                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
             <div className="flex justify-center items-center h-64">
                <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
             </div>
        ) : error ? (
            <div className="p-4 bg-red-50 text-red-600 rounded-xl border border-red-200 text-center">
                {error}
            </div>
        ) : (
            <div ref={printRef} className="print-content">
                {/* Print Header (Only visible in print) */}
                <div className="hidden print:block mb-6 text-center">
                    <h2 className="text-xl font-bold text-gray-800">รายงานสรุปการซื้อ</h2>
                    <p className="text-gray-500 text-sm">พิมพ์เมื่อ: {moment().format("D MMM YYYY HH:mm")}</p>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-4 rounded-tl-lg">หมายเลขบิล</th>
                                    <th className="px-6 py-4">ชื่อลูกค้า</th>
                                    <th className="px-6 py-4 text-center">วิธีชำระเงิน</th>
                                    <th className="px-6 py-4 text-right">ราคาสุทธิ</th>
                                    <th className="px-6 py-4 text-center">สถานะ</th>
                                    <th className="px-6 py-4">วันเปิดบิล</th>
                                    <th className="px-6 py-4 print:hidden rounded-tr-lg w-10"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredRows.length > 0 ? (
                                    filteredRows.map((row) => (
                                        <tr key={row.id} className="hover:bg-gray-50/80 transition-colors group">
                                            <td className="px-6 py-4 font-medium text-gray-800">{row.billNumber}</td>
                                            <td className="px-6 py-4 text-gray-600">{row.customerName || "-"}</td>
                                            <td className="px-6 py-4 text-center text-gray-600">{row.paymentMethod}</td>
                                            <td className="px-6 py-4 text-right font-medium text-gray-800">{formatNumber(row.totalNetPrice)}</td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(row.status)}`}>
                                                    {getStatusLabel(row.status)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-gray-500">{row.displayDate}</td>
                                            <td className="px-6 py-4 print:hidden text-center">
                                                <button 
                                                    onClick={() => navigate("/reports/purchases/" + row.transactionId)}
                                                    className="text-gray-400 hover:text-green-600 transition-colors p-1 rounded-full hover:bg-green-50"
                                                    title="ดูรายละเอียด"
                                                >
                                                    <SubjectIcon fontSize="small" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                                            ไม่พบข้อมูล
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
}
